import { CloudUploadOutlined, FileOutlined } from '@ant-design/icons';
import DataTable from '@portal/components/DataTable/DataTable';

import { PayableActions, ReceivableActions } from '@portal/actions';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedRadio from '@portal/components/AdvancedRadio/AdvancedRadio';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import CnabErrorModal from '@portal/components/MessageModal/MessageModal';
import { payment } from '@portal/config/app.json';
import { getRouteStackPath } from '@portal/config/routes';
import { ValidateIsNan } from '@portal/enum/validateIsNan';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import {
  FileTypePayable,
  PayableType,
  PaymentMode,
} from '@portal/models/enumerators';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { PayablePreview, ReceivablePreview } from '@portal/models/module';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { getEnumDescription } from '@portal/utils/enum';
import { moneyFormat } from '@portal/utils/format';
import truncateTextWithEllipsis from '@portal/utils/truncateTextWithEllipsis';
import { Tabs, Tooltip } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

let fileInput: HTMLInputElement | null;

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'asc',
  limit: 10,
};

enum TabIndexEnum {
  UPLOAD = '1',
  PREVIEW = '2',
}

const GeneratePaymentByFile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const location = useLocation<{ mode: PaymentMode }>();
  const {
    payable: { payablePreview },
    receivable: { receivablePreview },
    user: { me },
    paymentAccount: { selectedPaymentAccount },
  } = useReduxState();

  const [tabKey, setTabKey] = useState<string>('1');
  const [userFile, setUserFile] = useState<File>();
  const [advancedFilters, setAdvancedFilters] = useState(initialValues);
  const [openModal, setOpenModal] = useState(false);
  const [fileType, setFileType] = useState<FileTypePayable>(
    FileTypePayable.CNAB
  );
  const [transactionsType, setTransactionsType] = useState<PayableType>(
    PayableType.TRANSFER
  );
  const [referenceDate, setReferenceDate] = useState(
    DateTime.now().toISODate()
  );
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const handleDispatch = async (pinValue: string) => {
    const encryptedPin = await encrypt(pinValue);
    switch (location.state.mode) {
      case PaymentMode.PAYABLE:
        if (payablePreview && payablePreview.bucket && userFile) {
          dispatch(
            PayableActions.createPayable(
              payablePreview.bucket,
              userFile.name,
              encryptedPin,
              selectedPaymentAccount !== null
                ? selectedPaymentAccount.id
                : me.account.paymentAccounts.find(
                    (account) => account.type === PaymentAccountType.ACCOUNT
                  )?.id ?? '',
              fileType,
              transactionsType,
              payment.payablePaymentByFileDate ? undefined : referenceDate
            )
          );
          dispatch(PayableActions.cleanPreview());
          sessionStorage.setItem('payable_redirect', 'true');
          navigate(getRouteStackPath('PAYABLE', 'ACCOUNT_PAYABLE'));
        }
        break;

      case PaymentMode.RECEIVABLE:
        if (receivablePreview && receivablePreview.bucket && userFile) {
          dispatch(
            ReceivableActions.createReceivable(
              receivablePreview?.bucket,
              userFile.name,
              encryptedPin
            )
          );
          dispatch(ReceivableActions.cleanPreview());
          navigate(getRouteStackPath('RECEIVABLE', 'ACCOUNT_RECEIVABLE'));
        }
        break;

      default:
        break;
    }
  };

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.currentTarget.files) {
      const files = event.currentTarget.files[0];
      setUserFile(files);
    }
  };

  const handleTabChange = (newTabKey: string) => {
    setTabKey(newTabKey);
  };

  const changeCnabFile = () => {
    dispatch(PayableActions.cleanPreview());
    setTabKey('1');
    setUserFile(undefined);
    setIsErrorModalVisible(false);
  };

  const cancelCnabFile = () => {
    dispatch(PayableActions.cleanPreview());
    setIsErrorModalVisible(false);
    navigate(getRouteStackPath('PAYABLE', 'ACCOUNT_PAYABLE'));
  };

  const validatePayableForm = () =>
    !(
      referenceDate &&
      (DateTime.fromISO(referenceDate).diffNow('day').days > -1 ||
        DateTime.fromISO(referenceDate).hasSame(DateTime.now(), 'day')) &&
      transactionsType &&
      fileType
    );

  const goBack = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (userFile) {
      switch (location.state.mode) {
        case PaymentMode.PAYABLE:
          dispatch(
            PayableActions.uploadAndPreviewPayables(
              userFile,
              userFile.name.split('.')[1],
              userFile.name,
              fileType ?? FileTypePayable.CNAB,
              selectedPaymentAccount !== null
                ? selectedPaymentAccount.id
                : me.account.paymentAccounts.find(
                    (account) => account.type === PaymentAccountType.ACCOUNT
                  )?.id ?? ''
            )
          );
          break;

        case PaymentMode.RECEIVABLE:
          dispatch(
            ReceivableActions.uploadAndPreviewReceivable(
              userFile,
              userFile.name.split('.')[1],
              userFile.name
            )
          );
          break;

        default:
          break;
      }
      fileInput = null;
    }
    setAdvancedFilters(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userFile, selectedPaymentAccount?.id]);

  useEffect(
    () => () => {
      switch (location.state.mode) {
        case PaymentMode.PAYABLE:
          dispatch(PayableActions.cleanPreview());
          break;

        case PaymentMode.RECEIVABLE:
          dispatch(ReceivableActions.cleanPreview());
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (payablePreview || receivablePreview) {
      if (
        location.state.mode === PaymentMode.PAYABLE &&
        payablePreview?.rows?.length &&
        payablePreview?.rows?.[0].paymentAccount &&
        payablePreview?.rows?.[0].paymentAccount?.account &&
        payablePreview?.rows?.[0].paymentAccount?.account.taxIdentifier !==
          me.account.taxIdentifier
      ) {
        setIsErrorModalVisible(true);
      }
      setTabKey('2');
    }
  }, [
    payablePreview,
    receivablePreview,
    me.account.taxIdentifier,
    location.state.mode,
  ]);

  const renderValuesHeader = (
    mode: PaymentMode,
    payablePreview: PayablePreview | null,
    receivablePreview: ReceivablePreview | null,
    translate: (key: string) => string,
    moneyFormat: (value: number) => string
  ) => {
    const renderItem = (labelKey: string, value: number) => (
      <div className="generate-by-modal__values-header__inner">
        <h3>{translate(labelKey)}</h3>
        <p>{moneyFormat(value)}</p>
      </div>
    );

    switch (mode) {
      case PaymentMode.PAYABLE:
        return (
          <div className="generate-by-modal__values-header">
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.ALL',
              payablePreview?.total?.ALL ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.P2P',
              payablePreview?.total?.P2P ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.TED',
              payablePreview?.total?.TED ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.PIX',
              payablePreview?.total?.PIX ?? 0
            )}
          </div>
        );

      case PaymentMode.RECEIVABLE:
        return (
          <div className="generate-by-modal__values-header">
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.ALL',
              receivablePreview?.total?.ALL ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.P2P',
              receivablePreview?.total?.P2P ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.TED',
              receivablePreview?.total?.TED ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.PIX',
              receivablePreview?.total?.PIX ?? 0
            )}
            {renderItem(
              'PAGES.PAYMENT.GENERATE_BY_FILE.HEADER_LABEL.BILLET',
              receivablePreview?.total?.BILLET ?? 0
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const tabOptions = [
    {
      label: translate('PAGES.PAYMENT.GENERATE_BY_FILE.TABS.FILE_IMPORT'),
      key: TabIndexEnum.UPLOAD,
      children: (
        <>
          {location.state.mode === PaymentMode.PAYABLE &&
          payment.payableFileType ? (
            <div className="generate-by-modal__radio">
              <h3>
                {translate('PAGES.PAYMENT.GENERATE_BY_FILE.LABELS.FILE_TYPE')}
              </h3>
              <AdvancedRadio
                name={'fileType'}
                row
                value={`${fileType}`}
                onChange={(type: string) => setFileType(parseInt(type, 10))}
                options={[
                  {
                    value: `${FileTypePayable.CNAB}`,
                    name: translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.FILE_TYPE_OPTIONS.CNAB'
                    ),
                  },
                  {
                    value: `${FileTypePayable.CSV}`,
                    name: translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.FILE_TYPE_OPTIONS.CSV'
                    ),
                  },
                ]}
              />
            </div>
          ) : null}
          {location.state.mode === PaymentMode.PAYABLE &&
          payment.payableTransactionType ? (
            <div className="generate-by-modal__radio">
              <h3>
                {translate(
                  'PAGES.PAYMENT.GENERATE_BY_FILE.LABELS.TRANSFER_TYPE'
                )}
              </h3>
              <AdvancedRadio
                name={'transactionsType'}
                value={`${transactionsType}`}
                row
                onChange={(type: string) =>
                  setTransactionsType(parseInt(type, 10))
                }
                options={[
                  {
                    value: `${PayableType.TRANSFER}`,
                    name: translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.TRANSACTIONS_TYPE_OPTIONS.TRANSFER'
                    ),
                  },
                  {
                    value: `${PayableType.SALARY_PAYMENT}`,
                    name: translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.TRANSACTIONS_TYPE_OPTIONS.SALARY_PAYMENT'
                    ),
                  },
                ]}
              />
            </div>
          ) : null}
          {location.state.mode === PaymentMode.PAYABLE &&
          payment.payablePaymentDate &&
          !payment.payablePaymentByFileDate ? (
            <div>
              <AdvancedDateTimePicker
                disablePast
                time={false}
                onlyDate
                label={translate(
                  'PAGES.PAYMENT.GENERATE_BY_FILE.LABELS.REFERENCE_DATE'
                )}
                value={
                  referenceDate
                    ? DateTime.fromISO(referenceDate).toISODate()
                    : null
                }
                onChange={(date) => {
                  if (date) {
                    setReferenceDate(date.toISODate());
                  }
                }}
              />
            </div>
          ) : null}
          <div className="generate-payslip-by-file__upload">
            <div className="generate-payslip-by-file__upload__icon">
              <FileOutlined />
            </div>
            <div className="generate-by-modal__file-import-header">
              <h3>
                {fileType === FileTypePayable.CNAB
                  ? translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.HEADER_TITLE_CNAB'
                    )
                  : translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.HEADER_TITLE_CSV'
                    )}
              </h3>
              <h4>
                {fileType === FileTypePayable.CNAB
                  ? translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.HEADER_DESCRIPTION_CNAB'
                    )
                  : translate(
                      'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.HEADER_DESCRIPTION_CSV'
                    )}
              </h4>
            </div>
            <div className="generate-by-modal__button">
              <AdvancedButton
                endIcon={<CloudUploadOutlined />}
                text={translate(
                  'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.UPLOAD_FILE'
                )}
                onClick={() => {
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
                disabled={
                  location.state.mode === PaymentMode.PAYABLE
                    ? validatePayableForm()
                    : false
                }
              />
              <h4 className="generate-by-modal__button__text">
                {translate(
                  'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.MAX_FILE_SIZE'
                )}
              </h4>
              <input
                ref={(inputRef) => (fileInput = inputRef)}
                className="generate-by-modal__label__input"
                type="file"
                accept={fileType === FileTypePayable.CNAB ? '.rem' : '.csv'}
                onChange={onFileSelect}
                onClick={(event) => {
                  event.currentTarget.value = '';
                }}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      label: translate('PAGES.PAYMENT.GENERATE_BY_FILE.TABS.REVIEW'),
      key: TabIndexEnum.PREVIEW,
      disabled: !payablePreview,
      children: (
        <>
          {renderValuesHeader(
            location.state.mode,
            payablePreview,
            receivablePreview,
            translate,
            moneyFormat
          )}
          <div className="generate-by-modal__table">
            <DataTable
              rows={(() => {
                switch (location.state.mode) {
                  case PaymentMode.PAYABLE:
                    return payablePreview?.rows ?? [];

                  case PaymentMode.RECEIVABLE:
                    return receivablePreview?.rows ?? [];

                  default:
                    return [];
                }
              })()}
              getRowId={(row) => row.lineId}
              columns={[
                {
                  field: 'id',
                  flex: 1,
                  headerName: 'id',
                  sortable: false,
                  hide: true,
                },
                {
                  field:
                    location.state.mode === PaymentMode.PAYABLE
                      ? 'payeeName'
                      : 'name',
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.NAME'
                  ),
                  renderCell: (column) => (
                    <Tooltip
                      title={
                        location.state.mode === PaymentMode.PAYABLE
                          ? column.row.payeeName
                          : column.row.name
                      }
                    >
                      {truncateTextWithEllipsis(
                        location.state.mode === PaymentMode.PAYABLE
                          ? column.row.payeeName
                          : column.row.name,
                        15
                      )}
                    </Tooltip>
                  ),
                },
                {
                  field: 'payeeTaxIdentifier',
                  width: 150,
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.TAX_IDENTIFIER'
                  ),
                  renderCell: (column) => (
                    <>
                      {maskCpfOrCnpj(
                        `${
                          location.state.mode === PaymentMode.PAYABLE
                            ? column.row.payeeTaxIdentifier
                            : column.row.txId
                        }`
                      )}
                    </>
                  ),
                },
                {
                  field: 'payeeAgency',
                  width: 80,
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.BANK_BRANCH'
                  ),
                  hide: location.state.mode === PaymentMode.RECEIVABLE,
                  renderCell: (column) => (
                    <>
                      {column.row.payeeAgency === ValidateIsNan.NAN
                        ? translate(
                            'PAGES.PAYMENT.GENERATE_BY_FILE.MESSAGE.UNINFORMED'
                          )
                        : column.row.payeeAgencyDigit
                        ? `${column.row.payeeAgency} - ${column.row.payeeAgencyDigit}`
                        : column.row.payeeAgency}
                    </>
                  ),
                },
                {
                  field: 'payeeAccount',
                  width: 120,
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  hide: location.state.mode === PaymentMode.RECEIVABLE,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.BANK_ACCOUNT'
                  ),
                  renderCell: (column) => (
                    <>
                      {column.row.payeeAccount === ValidateIsNan.NAN
                        ? translate(
                            'PAGES.PAYMENT.GENERATE_BY_FILE.MESSAGE.UNINFORMED'
                          )
                        : column.row.payeeAccountDigit
                        ? `${column.row.payeeAccount} - ${column.row.payeeAccountDigit}`
                        : column.row.payeeAccount}
                    </>
                  ),
                },
                {
                  field: 'type',
                  flex: 1,
                  sortable: false,
                  align: 'center',
                  headerAlign: 'center',
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.TYPE'
                  ),
                  renderCell: (column) => (
                    <Tooltip
                      title={getEnumDescription(
                        column.row.type,
                        'TRANSACTION_TYPE'
                      )}
                    >
                      {truncateTextWithEllipsis(
                        getEnumDescription(
                          column.row.type,
                          'TRANSACTION_TYPE'
                        ) as string,
                        15
                      )}
                    </Tooltip>
                  ),
                },
                {
                  field: 'amount',
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  align: 'center',
                  headerAlign: 'center',
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.AMOUNT'
                  ),
                  renderCell: (column) => <>{moneyFormat(column.row.amount)}</>,
                },
                {
                  field: 'referenceDate',
                  flex: 1,
                  align: 'center',
                  headerAlign: 'center',
                  sortable: false,
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.REFERENCE_DATE'
                  ),
                  renderCell: (column) => (
                    <Tooltip
                      title={
                        column.row.referenceDate ||
                        translate('APPLICATION.NO_INFORMATION')
                      }
                    >
                      {truncateTextWithEllipsis(
                        column.row.referenceDate ||
                          translate('APPLICATION.NO_INFORMATION'),
                        15
                      )}
                    </Tooltip>
                  ),
                },
                {
                  field: 'status',
                  flex: 1,
                  sortable: false,
                  filterable: false,
                  headerName: translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.COLUMNS.STATUS'
                  ),
                  renderCell: (column) => (
                    <Tooltip
                      title={getEnumDescription(
                        column.row.status,
                        'PAYABLE_STATUS'
                      )}
                    >
                      {truncateTextWithEllipsis(
                        getEnumDescription(
                          column.row.status,
                          'PAYABLE_STATUS'
                        ) as string,
                        15
                      )}
                    </Tooltip>
                  ),
                },
              ]}
              page={advancedFilters.page}
              pageSize={advancedFilters.pageSize}
              onChange={(filters) => {
                const searchFilters = {
                  ...advancedFilters,
                  ...filters,
                };
                setAdvancedFilters(searchFilters);
              }}
              rowCount={(() => {
                switch (location.state.mode) {
                  case PaymentMode.PAYABLE:
                    return payablePreview?.rows.length ?? 0;

                  case PaymentMode.RECEIVABLE:
                    return receivablePreview?.rows.length ?? 0;

                  default:
                    return 0;
                }
              })()}
              orderBy={advancedFilters.orderBy}
              sort={advancedFilters.sort}
              paginationMode="client"
            />
            <div className="generate-by-modal__buttons">
              <div className="generate-by-modal__button-go-back">
                <AdvancedButton
                  text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
                  onClick={() => goBack()}
                  variant="outlined"
                />
              </div>
              <div className="generate-by-modal__button">
                <AdvancedButton
                  text={translate(
                    'PAGES.PAYMENT.GENERATE_BY_FILE.BUTTONS.CONFIRM'
                  )}
                  onClick={() => setOpenModal(true)}
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate(
              `PAGES.PAYMENT.ACCOUNT_${location.state.mode}.TITLE`
            ),
            url: getRouteStackPath(
              location.state.mode,
              `ACCOUNT_${location.state.mode}`
            ),
          },
          {
            active: false,
            title: translate(
              `PAGES.PAYMENT.GENERATE_BY_FILE.TABLE_TITLE_${location.state.mode}`
            ),
          },
        ]}
      />
      <div className="generate-by-modal">
        <AdvancedPanelContentHeader
          testId="generate-payment-by-file-header"
          title={translate(
            `PAGES.PAYMENT.GENERATE_BY_FILE.TABLE_TITLE_${location.state.mode}`
          )}
          description={translate(
            `PAGES.PAYMENT.GENERATE_BY_FILE.TABLE_DESCRIPTION_${location.state.mode}`
          )}
          goBackAction={() => navigate.back()}
        />
        <AdvancedCard testId="generate-payment-by-file-card">
          <Tabs
            defaultActiveKey="1"
            activeKey={tabKey}
            type="card"
            onChange={(key) => handleTabChange(key)}
          >
            {tabOptions.map((tab) => (
              <Tabs.TabPane
                tab={tab.label}
                key={tab.key}
                disabled={tab.disabled}
              >
                {tab.children}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </AdvancedCard>
      </div>
      <ConfirmationModal
        visible={openModal}
        setVisible={setOpenModal}
        handleConfirmation={handleDispatch}
        title={translate('PAGES.PAYMENT.GENERATE_BY_FILE.VALIDATION_TITLE')}
        text={translate('PAGES.PAYMENT.GENERATE_BY_FILE.VALIDATION_MESSAGE')}
      />
      <CnabErrorModal
        visible={isErrorModalVisible}
        setVisible={setIsErrorModalVisible}
        onConfirm={changeCnabFile}
        onCancel={cancelCnabFile}
        buttonCancelText={translate(
          'PAGES.PAYMENT.GENERATE_BY_FILE.CNAB_ERROR_MODAL.CANCEL_BUTTON'
        )}
        buttonOkText={translate(
          'PAGES.PAYMENT.GENERATE_BY_FILE.CNAB_ERROR_MODAL.CONFIRM_BUTTON'
        )}
        message={translate(
          'PAGES.PAYMENT.GENERATE_BY_FILE.CNAB_ERROR_MODAL.MODAL_TEXT'
        )}
        closable={false}
      />
    </>
  );
};

export default GeneratePaymentByFile;
